import { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Chip, TextField, Avatar, CircularProgress, Alert, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { getTicketById, addMessageToTicket, closeTicket } from '../services/ticketService';
import type { Ticket, TicketMessage } from '../types/ticket';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%'
}));

const TicketDetails = () => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const [replyMessage, setReplyMessage] = useState('');
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch ticket and messages
  useEffect(() => {
    const fetchTicketData = async () => {
      if (!ticketId) {
        setError('Ticket ID is missing');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const ticketResponse = await getTicketById(ticketId);

        console.log('Ticket response:', ticketResponse);

        // Handle different response structures - ticket can be in data.ticket or data directly
        if (ticketResponse.success && ticketResponse.data) {
          // The API returns ticket directly in data, not in data.ticket
          const ticket = (ticketResponse.data as any).ticket || ticketResponse.data;
          if (ticket && (ticket.id || ticket.ticket_id)) {
            setTicket(ticket);
            
            // Messages are included in the ticket response
            if (ticket.messages && Array.isArray(ticket.messages)) {
              setMessages(ticket.messages);
            }
          } else {
            throw new Error('Ticket data not found in response');
          }
        } else {
          throw new Error(ticketResponse.message || 'Failed to fetch ticket');
        }
      } catch (err) {
        console.error('Error fetching ticket data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load ticket');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicketData();
  }, [ticketId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return { bg: '#DBEAFE', color: '#1E40AF' };
      case 'in_progress': return { bg: '#FEF3C7', color: '#92400E' };
      case 'resolved': return { bg: '#D1FAE5', color: '#065F46' };
      case 'closed': return { bg: '#E5E7EB', color: '#374151' };
      default: return { bg: '#E5E7EB', color: '#374151' };
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return { bg: '#FEE2E2', color: '#991B1B' };
      case 'high': return { bg: '#FED7AA', color: '#9A3412' };
      case 'medium': return { bg: '#FEF3C7', color: '#92400E' };
      case 'low': return { bg: '#E5E7EB', color: '#374151' };
      default: return { bg: '#E5E7EB', color: '#374151' };
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !ticketId) return;

    setIsSending(true);
    setError(null);

    try {
      const response = await addMessageToTicket(ticketId, {
        message: replyMessage.trim()
      });

      if (response.success && response.data?.message) {
        setMessages([...messages, response.data.message]);
        setReplyMessage('');
        setSuccess('Message sent successfully!');
      } else {
        throw new Error(response.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!ticketId || !ticket) return;

    if (ticket.status === 'closed') {
      return;
    }

    const confirmMessage = 'Are you sure you want to close this ticket? This will mark it as resolved.';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsClosing(true);
    setError(null);

    try {
      const response = await closeTicket(ticketId);

      if (response.success) {
        // Update ticket status
        const updatedTicket = (response.data as any)?.ticket || response.data;
        if (updatedTicket) {
          setTicket({ ...ticket, status: 'closed' as const });
        } else {
          // If response doesn't include ticket, just update status
          setTicket({ ...ticket, status: 'closed' as const });
        }
        setSuccess('Ticket closed successfully!');
      } else {
        throw new Error(response.message || 'Failed to close ticket');
      }
    } catch (err) {
      console.error('Error closing ticket:', err);
      setError(err instanceof Error ? err.message : 'Failed to close ticket');
    } finally {
      setIsClosing(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Sidebar user={userData} />
        <MainContent>
          <DashboardHeader
            businessName={businessName}
            userAvatar={userData.avatar}
            businessLogo={userData.businessLogo}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        </MainContent>
      </PageContainer>
    );
  }

  if (error && !ticket) {
    return (
      <PageContainer>
        <Sidebar user={userData} />
        <MainContent>
          <DashboardHeader
            businessName={businessName}
            userAvatar={userData.avatar}
            businessLogo={userData.businessLogo}
          />
          <ContentArea>
            <Alert severity="error">{error}</Alert>
            <Button onClick={() => navigate('/help')} sx={{ mt: 2 }}>
              Back to Help Center
            </Button>
          </ContentArea>
        </MainContent>
      </PageContainer>
    );
  }

  if (!ticket) {
    return null;
  }

  const statusColors = getStatusColor(ticket.status);
  const priorityColors = getPriorityColor(ticket.priority);

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Back Button */}
          <Button
            startIcon={<ArrowLeft size={20} />}
            onClick={() => navigate('/help')}
            sx={{
              textTransform: 'none',
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.875rem',
              mb: 3,
              '&:hover': {
                bgcolor: '#F3F4F6',
                color: '#111827'
              }
            }}
          >
            Back to Help Center
          </Button>

          {/* Ticket Header */}
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '12px',
              p: 3,
              mb: 3,
              border: '1px solid #E5E7EB'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#6B7280'
                }}
              >
                {ticket.ticket_number || ticket.id}
              </Typography>
              <Chip
                label={ticket.priority.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: priorityColors.bg,
                  color: priorityColors.color,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 20,
                  borderRadius: '4px'
                }}
              />
              <Chip
                label={ticket.status.replace('_', ' ').toUpperCase()}
                size="small"
                sx={{
                  bgcolor: statusColors.bg,
                  color: statusColors.color,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 20,
                  borderRadius: '4px'
                }}
              />
              <Chip
                label={ticket.category}
                size="small"
                sx={{
                  bgcolor: '#F3F4F6',
                  color: '#374151',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 20,
                  borderRadius: '4px'
                }}
              />
            </Stack>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#111827',
                mb: 1
              }}
            >
              {ticket.subject}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                mb: 2
              }}
            >
              Created on {formatTimestamp(ticket.created_at)} • Last updated {formatTimestamp(ticket.updated_at)}
            </Typography>
            
            {/* Close Ticket Button */}
            {ticket.status !== 'closed' && (
              <Button
                variant="outlined"
                startIcon={<CheckCircle size={18} />}
                onClick={handleCloseTicket}
                disabled={isClosing}
                sx={{
                  textTransform: 'none',
                  color: '#059669',
                  borderColor: '#059669',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: '#ECFDF5',
                    borderColor: '#047857'
                  },
                  '&.Mui-disabled': {
                    borderColor: '#D1D5DB',
                    color: '#9CA3AF'
                  }
                }}
              >
                {isClosing ? 'Closing...' : 'Close Ticket'}
              </Button>
            )}
          </Box>

          {/* Conversation Thread */}
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '12px',
              p: 3,
              mb: 3,
              border: '1px solid #E5E7EB'
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                color: '#111827',
                mb: 3
              }}
            >
              Conversation
            </Typography>

            <Stack spacing={3}>
              {messages.map((message, index) => (
                <Box key={message.id}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      src={message.sender_avatar || (message.sender_type === 'support' ? 'https://ui-avatars.com/api/?name=Support&background=4F46E5&color=fff' : undefined)}
                      sx={{
                        width: 40,
                        height: 40
                      }}
                    >
                      {!message.sender_avatar && message.sender_type === 'user' && (message.first_name || message.last_name)
                        ? (message.first_name?.charAt(0) || message.last_name?.charAt(0) || 'U').toUpperCase()
                        : message.sender_type === 'support' ? 'S' : 'U'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#111827'
                          }}
                        >
                          {message.sender_type === 'support' 
                            ? 'Support Team'
                            : (message.first_name || message.last_name)
                              ? `${message.first_name || ''} ${message.last_name || ''}`.trim()
                              : message.sender_name || 'User'}
                        </Typography>
                        {message.sender_type === 'support' && (
                          <Chip
                            label="SUPPORT"
                            size="small"
                            sx={{
                              bgcolor: '#EEF2FF',
                              color: '#4F46E5',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: 18,
                              borderRadius: '4px'
                            }}
                          />
                        )}
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#9CA3AF'
                          }}
                        >
                          {formatTimestamp(message.created_at)}
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          bgcolor: message.sender_type === 'user' ? '#F9FAFB' : '#EEF2FF',
                          borderRadius: '8px',
                          p: 2,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#374151',
                            lineHeight: 1.6
                          }}
                        >
                          {message.message}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                  {index < messages.length - 1 && (
                    <Box
                      sx={{
                        height: 1,
                        bgcolor: '#F3F4F6',
                        my: 3,
                        ml: 7
                      }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Reply Box */}
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '12px',
              p: 3,
              border: '1px solid #E5E7EB'
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                color: '#111827',
                mb: 2
              }}
            >
              Add Reply
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Type your message here..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#E5E7EB'
                  },
                  '&:hover fieldset': {
                    borderColor: '#D1D5DB'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4F46E5'
                  }
                }
              }}
            />
            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Button
                variant="contained"
                endIcon={!isSending ? <Send size={18} /> : null}
                onClick={handleSendReply}
                disabled={!replyMessage.trim() || isSending}
                sx={{
                  bgcolor: '#4F46E5',
                  color: 'white',
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#4338CA',
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    bgcolor: '#D1D5DB',
                    color: '#9CA3AF'
                  }
                }}
              >
                {isSending ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                    Sending...
                  </Box>
                ) : (
                  'Send Reply'
                )}
              </Button>
            </Stack>
          </Box>
        </ContentArea>
      </MainContent>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default TicketDetails;

